// Third-party imports
import React from 'react';
import {
  SimpleForm,
  useRecordContext,
} from 'react-admin';
import { Box } from '@mui/material';

// Local imports - types
import type { RepairDetail } from '@src/types';
import { RejectionReasons } from '@src/types/repair';

// Local imports - components
import ShowStatusSection from './ShowStatusSection';
import ShowItemsSection from './ShowItemsSection';
import EditStatusSection from './EditStatusSection';
import EditItemsSection from './EditItemsSection';
import CustomerDisplay from '../shared/CustomerDisplay';
import TimelineDisplay from '../shared/TimelineDisplay';

/**
 * Form values for repair review submission.
 *
 * Note: 'APPROVED_FREE' in repairStatus is a UI-only convenience state.
 * It does not exist in the backend database. Before submission, it's transformed
 * to 'APPROVED' with all prices set to $0 (see repairsDataProvider.ts).
 */
interface FormValues {
  repairStatus: 'APPROVED' | 'APPROVED_FREE' | 'REJECTED';
  shippingPrice: number;
  repairRejectionReason?: RejectionReasons;
  issueStates?: Record<string, 'APPROVED' | 'REJECTED'>;
  issuePrices?: Record<string, number>;
  issueRejectionReasons?: Record<string, RejectionReasons>;
}

interface RepairFormErrors {
  repairStatus?: string;
  repairRejectionReason?: string;
  issueRejectionReasons?: Record<string, string>;
}

const RepairForm = () => {
  const record = useRecordContext<RepairDetail>();

  if (!record) return null;

  return (
    <SimpleForm
      defaultValues={{
        repairStatus: 'APPROVED',
        shippingPrice: record.shippingPrice || 0,
        repairRejectionReason: '',
        issueStates: {},
        issuePrices: {},
        issueRejectionReasons: {},
      }}
      toolbar={false}
      onKeyDown={(e) => {
        /* Since form submission results in a customer notification
          we want to avoid accidental submissions that may come
          from a user hitting enter. They need to explicitly
          click "Submit & Notify Customer" */
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      validate={((values: FormValues) => {
        const errors: RepairFormErrors = {};

        // Validate repair status selection
        // Edge case since RadioButtonGroupInput should always have a value
        if (!values.repairStatus) {
          errors.repairStatus = 'Please select a repair status';
        }

        // Validate rejection reason if repair is rejected
        if (values.repairStatus === 'REJECTED' && !values.repairRejectionReason) {
          errors.repairRejectionReason = 'Rejection reason is required when rejecting repair';
        }

        // Check if all issues are rejected when trying to approve
        if (values.repairStatus !== 'REJECTED' && values.issueStates) {
          const allIssuesRejected = record.repairItems?.every(item =>
            item.repairItemIssues?.every(issue =>
              values.issueStates?.[issue.uuid] === 'REJECTED'));

          if (allIssuesRejected) {
            errors.repairStatus = 'Cannot accept repair when all issues are rejected';
          }
        }

        // Validate issue rejection reasons
        if (values.issueStates && values.issueRejectionReasons) {
          const issueErrors: Record<string, string> = {};
          record.repairItems?.forEach((item) => {
            item.repairItemIssues?.forEach((issue) => {
              if (values.issueStates?.[issue.uuid] === 'REJECTED' &&
                !values.issueRejectionReasons?.[issue.uuid]) {
                issueErrors[issue.uuid] = 'Rejection reason required';
              }
            });
          });

          if (Object.keys(issueErrors).length > 0) {
            errors.issueRejectionReasons = issueErrors;
          }
        }

        // Validate that all issues have explicit states when repair is not rejected
        if (values.repairStatus !== 'REJECTED') {
          const hasUndefinedStates = record.repairItems?.some(item =>
            item.repairItemIssues?.some(issue =>
              !values.issueStates || values.issueStates[issue.uuid] === undefined));

          if (hasUndefinedStates) {
            errors.repairStatus = 'All issues must have an explicit approval or rejection status';
          }
        }

        return errors;
      }) as any}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        {record.status === 'Under Review' ? (
          <>
            <EditStatusSection />
            <EditItemsSection />
          </>
        ) : (
          <>
            <ShowStatusSection />
            <ShowItemsSection />
          </>
        )}
        <CustomerDisplay />
        <TimelineDisplay />
      </Box>
    </SimpleForm>
  );
};

export default RepairForm;
